// src/pages/guides/AIAttackReadinessGuide.js
// AI Attack Readiness Guide - SEO Guides Program

import React from 'react';
import useTrialCta from '../../hooks/useTrialCta';
import GuideByline from './GuideByline';
import GuideAnswer from './GuideAnswer';
import GuideFAQ, { buildFAQPageSchema } from './GuideFAQ';
import { Link } from 'react-router-dom';
import SEOHead from '../../components/common/SEOHead';
import { PublicHeader, PublicFooter } from '../../components/common';
import './Guides.css';

function AIAttackReadinessGuide() {
  const startTrial = useTrialCta('guide_trial:ai-attack-readiness');

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Is Your Business Ready for AI-Powered Attacks? A Practical Checklist",
    "description": "AI-enabled adversary operations are up 89% year-over-year. A practical 10-point checklist to assess your readiness against AI-powered threats in 2026.",
    "image": ["https://www.driftalarm.com/images/og-image.png"],
    "datePublished": "2026-04-25T00:00:00-04:00",
    "dateModified": "2026-08-26T00:00:00-04:00",
    "author": { "@type": "Organization", "name": "DriftAlarm", "url": "https://www.driftalarm.com" },
    "publisher": { "@type": "Organization", "name": "DriftAlarm", "url": "https://www.driftalarm.com", "logo": { "@type": "ImageObject", "url": "https://www.driftalarm.com/images/og-image.png" } },
    "url": "https://www.driftalarm.com/guides/ai-attack-readiness",
    "mainEntityOfPage": "https://www.driftalarm.com/guides/ai-attack-readiness",
    "keywords": ["AI attack readiness", "AI powered attacks checklist", "Mythos ready", "cybersecurity AI threats 2026"]
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.driftalarm.com" },
      { "@type": "ListItem", "position": 2, "name": "Security Guides", "item": "https://www.driftalarm.com/guides" },
      { "@type": "ListItem", "position": 3, "name": "AI Attack Readiness Checklist" }
    ]
  };

  return (
    <div className="guide-container">
      <SEOHead
        title="Is Your Business Ready for AI-Powered Attacks? A Checklist | DriftAlarm"
        description="AI-enabled adversary operations are up 89% year-over-year. A practical 10-point checklist to assess your readiness against AI-powered threats in 2026."
        path="/guides/ai-attack-readiness"
        ogType="article"
        jsonLd={[articleSchema, breadcrumbSchema, buildFAQPageSchema('/guides/ai-attack-readiness')].filter(Boolean)}
      />

      <PublicHeader activeLink="guides" />

      <main className="guide-content">
        <section className="guide-hero">
          <h1>Is Your Business Ready for AI-Powered Attacks? A Practical Checklist</h1>
          <GuideByline path="/guides/ai-attack-readiness" />
          <GuideAnswer path="/guides/ai-attack-readiness" />
          <p className="guide-hero-intro">
            AI-enabled adversary operations increased <strong>89% year-over-year</strong> according to CrowdStrike's
            2026 Global Threat Report. An autonomous AI agent reached #1 on HackerOne's US leaderboard in 2025, and
            an open-source offensive framework was used in a campaign against internet-facing appliances in 55
            countries. None of this changes what good security looks like — it changes how much the cadence
            matters. This guide is a practical 10-point checklist for assessing where you stand.
          </p>

          <div className="guide-hero-bullets">
            <div className="guide-hero-bullets-title">What You'll Learn</div>
            <ul>
              <li>Current AI threat landscape: 89% increase in AI-enabled operations</li>
              <li>XBOW: the autonomous agent that topped HackerOne's leaderboard</li>
              <li>CyberStrikeAI: confirmed attacks across 55 countries</li>
              <li>What the CSA's "Mythos-ready" guidance actually asks for</li>
              <li>A 10-point AI Attack Readiness Checklist with actionable guidance</li>
              <li>How DriftAlarm addresses each checklist item</li>
            </ul>
          </div>

          <span className="guide-card-read-time" style={{ display: 'inline-block', marginBottom: '20px' }}>12 min read</span>

          <div className="guide-cta-group">
            <button className="da-btn da-btn--primary" onClick={startTrial}>
              <span>Start Free 30-Day Trial</span>
              <span>&rarr;</span>
            </button>
            <Link to="/how-it-works" className="da-btn da-btn--secondary">See our methodology</Link>
          </div>
        </section>

        <nav className="guide-toc">
          <div className="guide-toc-title">In This Guide</div>
          <ul className="guide-toc-list">
            <li><a href="#threat-landscape">The AI Threat Landscape in 2026</a></li>
            <li><a href="#xbow">XBOW: AI Pentesting Goes Production</a></li>
            <li><a href="#cyberstrike">CyberStrikeAI: An Open-Source Framework in a Real Campaign</a></li>
            <li><a href="#mythos-ready">Building a "Mythos-Ready" Security Program</a></li>
            <li><a href="#checklist">10-Point AI Attack Readiness Checklist</a></li>
            <li><a href="#driftalarm-mapping">How DriftAlarm Addresses Each Item</a></li>
            <li><a href="#faq">Frequently Asked Questions</a></li>
            <li><a href="#sources">Sources</a></li>
            <li><a href="#related-guides">Related Security Guides</a></li>
          </ul>
        </nav>

        {/* Section 1: The AI Threat Landscape */}
        <section id="threat-landscape" className="guide-section">
          <h2>The AI Threat Landscape in 2026</h2>
          <p>
            The CrowdStrike 2026 Global Threat Report documented a fundamental shift in the cyber threat
            landscape. <strong>AI-enabled adversary operations increased 89% year-over-year</strong>, driven
            by three converging factors: the availability of powerful AI models, the proliferation of
            open-source offensive AI tools, and the dramatic reduction in skill required to launch
            sophisticated attacks.
          </p>

          <div className="callout callout-info">
            <div className="callout-title">89% year-over-year, per CrowdStrike's 2026 report</div>
            <div className="callout-content">
              <p>
                An 89% annual increase means AI-enabled adversary activity nearly doubled in a year. We are
                deliberately not projecting that rate forward — a single year's growth is not a trend line, and
                the number that matters for planning is not next year's volume but how quickly you would notice
                a change to your own perimeter.
              </p>
            </div>
          </div>

          <p>
            The nature of attacks is changing as fundamentally as their volume. AI-powered attacks are
            characterized by speed (exploitation within minutes of discovery), breadth (thousands of targets
            scanned simultaneously), and sophistication (multi-step attack chains that would previously
            require skilled human operators). The traditional assumption that small businesses are not
            worth targeting has been invalidated by the near-zero marginal cost of AI-powered scanning.
          </p>
        </section>

        {/* Section 2: XBOW */}
        <section id="xbow" className="guide-section">
          <h2>XBOW: AI Pentesting Is Production-Ready</h2>
          <p>
            In June 2025, an autonomous AI agent called <strong>XBOW reached #1 on HackerOne's US bug bounty
            leaderboard</strong>. This was not a controlled demo. XBOW competed against human security researchers
            on real bug bounty programs and reached the top of the US ranking.
          </p>
          <p>
            XBOW demonstrated that AI pentesting has crossed the threshold from research project to
            production-ready capability. The agent autonomously:
          </p>
          <ul>
            <li>Identified target applications and their technology stacks</li>
            <li>Performed reconnaissance and attack surface enumeration</li>
            <li>Discovered and validated exploitable vulnerabilities</li>
            <li>Generated proof-of-concept exploits</li>
            <li>Submitted properly formatted bug reports</li>
          </ul>

          <div className="callout callout-warning">
            <div className="callout-title">The Dual-Use Problem</div>
            <div className="callout-content">
              <p>
                XBOW's success on HackerOne demonstrates a defensive capability — finding bugs before
                attackers do. But the same technology, applied offensively, creates an autonomous attack
                agent that can discover and exploit vulnerabilities without human guidance. The techniques
                are identical. Only the intent differs.
              </p>
            </div>
          </div>

          <p>
            XBOW's achievement signals that the era of human-only penetration testing is ending. AI agents
            can now match or exceed human researchers in vulnerability discovery for many common vulnerability
            classes. For defenders, this means the volume and speed of probing against your attack surface is
            about to increase dramatically.
          </p>
        </section>

        {/* Section 3: CyberStrikeAI */}
        <section id="cyberstrike" className="guide-section">
          <h2>CyberStrikeAI: An Open-Source Framework Used in a Real Campaign</h2>
          <p>
            Where XBOW is the defensive application, <strong>CyberStrikeAI</strong> shows the offensive one. It is
            an <strong>open-source offensive framework published on GitHub</strong> — not a bespoke nation-state
            tool. Research from Team Cymru, reported in March 2026 with initial findings from Amazon Threat
            Intelligence, documented a suspected Russian-speaking actor using it to compromise
            <strong> more than 600 Fortinet FortiGate appliances across 55 countries</strong> between 20 January
            and 26 February 2026.
          </p>
          <p>
            Two details matter for planning. First, the 55 countries describe the <em>victim spread of that one
            campaign</em>, not the tool's installed base — a distinction worth keeping, because the campaign
            figure is the one that is actually evidenced. Second, the entry point was a class of internet-facing
            appliance, which is exactly the kind of asset that tends to sit outside a small team's patching
            routine.
          </p>

          <div className="callout callout-info">
            <div className="callout-title">The accessibility is the story, not the sophistication</div>
            <div className="callout-content">
              <p>
                The notable thing about this campaign is not that the tooling was advanced. It is that the
                framework was publicly available, and the way in was a reachable, unpatched edge appliance. That
                is a coverage problem before it is a sophistication problem — which is good news, because
                coverage is the part you can actually fix.
              </p>
            </div>
          </div>
        </section>

        {/* Section 4: Building a Mythos-ready security program (CSA) */}
        <section id="mythos-ready" className="guide-section">
          <h2>Building a "Mythos-Ready" Security Program</h2>
          <p>
            In April 2026 the Cloud Security Alliance published <em>The "AI Vulnerability Storm": Building a
            "Mythos-ready" Security Program</em>, setting out roughly eleven priority actions organized by
            horizon — this week, the next 45 days, and the next 12 months. It is worth reading in full;
            the link is in the sources below.
          </p>
          <p>
            Three of its themes carry most of the weight for a small IT team, and they are the ones this guide
            is built around. The framing below is ours, not CSA's:
          </p>

          <div className="step-cards">
            <div className="step-card">
              <div className="step-card-number">1</div>
              <div className="step-card-title">Assume Continuous Probing</div>
              <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary, #b0b0b0)', marginTop: '8px' }}>
                Your external attack surface is being scanned by AI-powered tools continuously. Design
                your security program around the assumption that every exposed service, port, and
                configuration will be tested for weaknesses daily — not annually.
              </p>
            </div>

            <div className="step-card">
              <div className="step-card-number">2</div>
              <div className="step-card-title">Minimize Time-to-Detect</div>
              <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary, #b0b0b0)', marginTop: '8px' }}>
                CrowdStrike put average eCrime breakout time at 29 minutes in its 2026 report, with the
                fastest it has ever observed at 27 seconds. Treat 29 minutes as the planning number and the
                27 seconds as the tail. Either way the implication is the same: shift investment from periodic
                assessment to continuous monitoring, and aim to detect attack-surface changes within hours.
              </p>
            </div>

            <div className="step-card">
              <div className="step-card-number">3</div>
              <div className="step-card-title">Reduce Attack Surface Aggressively</div>
              <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary, #b0b0b0)', marginTop: '8px' }}>
                Every internet-facing service is a target. Reduce your external footprint to the minimum
                required for business operations. Remove end-of-life software, close unnecessary ports,
                clean up unused DNS records, and decommission forgotten services. The best defense against
                AI-powered scanning is having fewer targets to scan.
              </p>
            </div>
          </div>
        </section>

        {/* Mid-article CTA */}
        <section className="guide-section">
          <div className="guide-cta-section">
            <h3>Assess Your AI Readiness Now</h3>
            <p>
              Use the checklist below to evaluate your organization. Then see how DriftAlarm addresses each
              item with continuous, automated monitoring of your external attack surface.
            </p>
            <div className="guide-cta-buttons">
              <button onClick={startTrial} className="da-btn da-btn--primary">
                <span>Start Free 30-Day Trial</span>
                <span>&rarr;</span>
              </button>
              <Link to="/how-it-works" className="da-btn da-btn--secondary">See How It Works</Link>
            </div>
          </div>
        </section>

        {/* Section 5: 10-Point Checklist */}
        <section id="checklist" className="guide-section">
          <h2>10-Point AI Attack Readiness Checklist</h2>
          <p>
            Score yourself honestly. Each item is binary — you either have the capability or you do not.
            A "sort of" or "we are working on it" counts as a no. AI-powered attackers will not wait for
            your roadmap.
          </p>

          <div className="step-cards">
            <div className="step-card">
              <div className="step-card-number">1</div>
              <div className="step-card-title">Do you know your full external attack surface?</div>
              <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary, #b0b0b0)', marginTop: '8px' }}>
                Can you enumerate every domain, subdomain, IP address, and cloud resource your organization
                exposes to the internet? This includes shadow IT, forgotten staging environments, marketing
                microsites, and legacy systems. If you cannot list them all, AI scanners already know more
                about your surface than you do.
              </p>
            </div>

            <div className="step-card">
              <div className="step-card-number">2</div>
              <div className="step-card-title">Are you monitoring for new subdomains and services daily?</div>
              <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary, #b0b0b0)', marginTop: '8px' }}>
                New subdomains can appear from dev teams spinning up test environments, marketing launching
                campaign sites, or attackers compromising your DNS. Daily monitoring ensures new assets are
                discovered and assessed within 24 hours — not at the next quarterly review.
              </p>
            </div>

            <div className="step-card">
              <div className="step-card-number">3</div>
              <div className="step-card-title">Can you detect configuration drift within hours?</div>
              <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary, #b0b0b0)', marginTop: '8px' }}>
                When a port opens, a TLS certificate downgrades, or a DNS record changes, do you know about
                it the same day? Configuration drift creates the conditions that AI-powered tools exploit.
                Detecting drift within hours means you can remediate before automated scanners weaponize the change.
              </p>
            </div>

            <div className="step-card">
              <div className="step-card-number">4</div>
              <div className="step-card-title">Do you have automated vulnerability scanning (not just annual pentests)?</div>
              <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary, #b0b0b0)', marginTop: '8px' }}>
                Annual pentests provide depth but miss 364 days of changes. Automated vulnerability scanning
                provides continuous coverage that catches new CVEs as they are published — not months later.
                With mean time-to-exploit now reported as negative — Mandiant's M-Trends 2026 puts it at
                roughly -7 days, meaning exploitation frequently precedes public disclosure — daily scanning is
                the minimum viable cadence.
              </p>
            </div>

            <div className="step-card">
              <div className="step-card-number">5</div>
              <div className="step-card-title">Are your DNS records clean (no dangling CNAMEs)?</div>
              <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary, #b0b0b0)', marginTop: '8px' }}>
                Dangling CNAME records — DNS entries pointing to decommissioned cloud resources — enable
                subdomain takeover attacks. An attacker can claim the orphaned resource and serve malicious
                content under your domain name. AI-powered tools actively scan for dangling CNAMEs at scale.
              </p>
            </div>

            <div className="step-card">
              <div className="step-card-number">6</div>
              <div className="step-card-title">Are all SSL/TLS certificates current and properly configured?</div>
              <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary, #b0b0b0)', marginTop: '8px' }}>
                Expired certificates, weak cipher suites, and deprecated protocol versions are all signals
                that AI scanners use to identify poorly maintained targets. Certificate issues also erode
                user trust and can break functionality. Monitor expiration dates, protocol versions, and
                certificate chain validity continuously.
              </p>
            </div>

            <div className="step-card">
              <div className="step-card-number">7</div>
              <div className="step-card-title">Is end-of-life software removed from internet-facing systems?</div>
              <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary, #b0b0b0)', marginTop: '8px' }}>
                End-of-life software receives no security patches. It is permanently vulnerable to any
                exploit discovered after its end-of-life date. AI vulnerability discovery accelerates the
                rate at which new exploits are found. Running EOL software on the internet is equivalent
                to leaving a door permanently unlocked.
              </p>
            </div>

            <div className="step-card">
              <div className="step-card-number">8</div>
              <div className="step-card-title">Do you have alerts for new open ports?</div>
              <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary, #b0b0b0)', marginTop: '8px' }}>
                Ports opened for maintenance, debugging, or deployment frequently remain open indefinitely.
                Each open port is an additional entry point that AI scanners will probe. Alerting on new
                open ports ensures that temporary exceptions do not become permanent attack surface.
              </p>
            </div>

            <div className="step-card">
              <div className="step-card-number">9</div>
              <div className="step-card-title">Can you answer "are we secure?" with data, not feelings?</div>
              <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary, #b0b0b0)', marginTop: '8px' }}>
                When your CEO or board asks about your security posture, can you provide a data-driven
                answer? Risk scores, vulnerability counts, drift trends, and patch coverage metrics tell a
                clear story. "I think we are fine" is not a security posture assessment — it is a hope.
              </p>
            </div>

            <div className="step-card">
              <div className="step-card-number">10</div>
              <div className="step-card-title">Do you have AI-powered analysis to prioritize remediation?</div>
              <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary, #b0b0b0)', marginTop: '8px' }}>
                With AI generating more vulnerability findings than ever, human-only triage creates a
                bottleneck. AI-powered risk scoring combines CVSS severity, EPSS exploitation probability,
                asset criticality, and environmental context to prioritize what matters most — not just what
                scored highest on a generic scale.
              </p>
            </div>
          </div>
        </section>

        {/* Section 6: How DriftAlarm Addresses Each Item */}
        <section id="driftalarm-mapping" className="guide-section">
          <h2>How DriftAlarm Addresses Each Checklist Item</h2>
          <p>
            DriftAlarm was built for the threat environment described in this guide. Here is how the
            platform maps to each item in the AI Attack Readiness Checklist:
          </p>

          <div className="guide-table-wrapper">
            <table className="guide-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Checklist Item</th>
                  <th>DriftAlarm Capability</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>1</strong></td>
                  <td>Know your full attack surface</td>
                  <td>Automated discovery scanning enumerates subdomains, IPs, ports, services, and technologies</td>
                </tr>
                <tr>
                  <td><strong>2</strong></td>
                  <td>Monitor for new subdomains daily</td>
                  <td>Daily DNS enumeration with alerts for new subdomain discovery</td>
                </tr>
                <tr>
                  <td><strong>3</strong></td>
                  <td>Detect configuration drift within hours</td>
                  <td>32 drift detection rules compare every scan against automated baselines</td>
                </tr>
                <tr>
                  <td><strong>4</strong></td>
                  <td>Automated vulnerability scanning</td>
                  <td>Daily vulnerability scanning across your internet-facing assets</td>
                </tr>
                <tr>
                  <td><strong>5</strong></td>
                  <td>Clean DNS records</td>
                  <td>Dangling CNAME detection and DNS drift monitoring across all records</td>
                </tr>
                <tr>
                  <td><strong>6</strong></td>
                  <td>SSL/TLS certificates current</td>
                  <td>Certificate expiration tracking, protocol version monitoring, cipher suite analysis</td>
                </tr>
                <tr>
                  <td><strong>7</strong></td>
                  <td>EOL software removed</td>
                  <td>Technology fingerprinting with end-of-life detection and alerts</td>
                </tr>
                <tr>
                  <td><strong>8</strong></td>
                  <td>Alerts for new open ports</td>
                  <td>Port drift detection alerts on any new open port with service identification</td>
                </tr>
                <tr>
                  <td><strong>9</strong></td>
                  <td>Data-driven security posture</td>
                  <td>Risk scores, trend charts, vulnerability counts, and weekly security reports</td>
                </tr>
                <tr>
                  <td><strong>10</strong></td>
                  <td>AI-powered prioritization</td>
                  <td>AI risk analysis combining CVSS, EPSS, asset context, and environmental factors</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Related Guides */}
        <GuideFAQ path="/guides/ai-attack-readiness" />

        <section id="sources" className="guide-section">
          <h2>Sources</h2>
          <ul>
            <li>
              CrowdStrike, <a href="https://www.crowdstrike.com/en-us/press-releases/2026-crowdstrike-global-threat-report/" target="_blank" rel="noopener noreferrer">
              2026 Global Threat Report</a> (February 2026) — the 89% year-over-year increase in AI-enabled
              adversary activity, and the 29-minute average / 27-second fastest eCrime breakout times.
            </li>
            <li>
              XBOW, <a href="https://xbow.com/blog/top-1-how-xbow-did-it" target="_blank" rel="noopener noreferrer">
              Top 1: how XBOW did it</a> (June 2025) — reaching #1 on HackerOne's US leaderboard.
            </li>
            <li>
              Team Cymru research with initial findings from Amazon Threat Intelligence, reported March 2026 —
              CyberStrikeAI used against 600+ FortiGate appliances across 55 countries, 20 Jan–26 Feb 2026.
            </li>
            <li>
              Cloud Security Alliance, <a href="https://cloudsecurityalliance.org/mythos-ciso" target="_blank" rel="noopener noreferrer">
              The "AI Vulnerability Storm": Building a "Mythos-ready" Security Program</a> (April 2026).
            </li>
            <li>
              Mandiant / Google Cloud, M-Trends 2026 — mean time-to-exploit reported as negative.
            </li>
          </ul>
        </section>

        <section id="related-guides" className="guide-section">
          <h2>Related Security Guides</h2>
          <p>
            AI readiness requires understanding the full threat landscape. These guides provide
            deeper coverage on the topics referenced in this checklist:
          </p>

          <div className="playbook-links">
            <Link to="/guides/ai-vulnerability-threats" className="playbook-link playbook-link-active">
              <span className="playbook-link-title">What AI Vulnerability Discovery Means for Your Business</span>
              <span className="playbook-link-arrow">&rarr;</span>
            </Link>
            <Link to="/guides/patch-window-collapse" className="playbook-link playbook-link-active">
              <span className="playbook-link-title">The Patch Window Is Dead: Why Continuous Monitoring Beats Annual Pentests</span>
              <span className="playbook-link-arrow">&rarr;</span>
            </Link>
            <Link to="/guides/attack-surface-monitoring" className="playbook-link playbook-link-active">
              <span className="playbook-link-title">Attack Surface Monitoring Guide</span>
              <span className="playbook-link-arrow">&rarr;</span>
            </Link>
            <Link to="/guides/configuration-drift" className="playbook-link playbook-link-active">
              <span className="playbook-link-title">Configuration Drift: Why Security Breaks Quietly</span>
              <span className="playbook-link-arrow">&rarr;</span>
            </Link>
            <Link to="/guides/dns-drift-detection" className="playbook-link playbook-link-active">
              <span className="playbook-link-title">DNS Drift Detection Guide</span>
              <span className="playbook-link-arrow">&rarr;</span>
            </Link>
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="guide-section">
          <h2>Start Assessing Your AI Readiness Today</h2>
          <p>
            Every item on this checklist that you cannot answer "yes" to is a gap that AI-powered attackers
            will find. DriftAlarm gives you the continuous visibility needed to close those gaps — starting
            with a free 30-day trial of the full platform.
          </p>

          <div className="guide-cta-group" style={{ marginTop: '40px' }}>
            <button className="da-btn da-btn--primary" onClick={startTrial}>
              <span>Start Free 30-Day Trial</span>
              <span>&rarr;</span>
            </button>
            <Link to="/upgrade" className="da-btn da-btn--secondary">View Pricing</Link>
          </div>
        </section>
      </main>

      <PublicFooter />
    </div>
  );
}

export default AIAttackReadinessGuide;
